import { bunja } from 'bunja';
import { atom } from 'jotai';

import Num from '@flipster/utils/num';
import { positionOrderSort } from '@flipster/utils/sort';

import { mapTable } from '..';
import type { PrexDecimal } from '../../../model/prex.shared.primitives';
import type { ConditionalOrderType } from '../../../model/prex.v2.r230522.order';
import type { PswapOrder } from '../../../model/prex.v2.r230522.tables.private_orders';
import { PswapOrder_fromJsonValue } from '../../../model/prex.v2.r230522.tables.private_orders';
import { r230522NormalSubSocketBunja } from '../../socket';
import { SubAccountIdScope } from '../../sub-account';
import { accountOrdersBunja } from './account-orders';

export interface PendingPosition extends PswapOrder {
  orderVolumeType: 'qty' | 'amount';
  qty: PrexDecimal;
  amount: PrexDecimal;
}

const zero: PrexDecimal = Num(0);

const baseOrdersBunja = bunja(() => {
  const { subscribe, tablesAtom } = bunja.use(r230522NormalSubSocketBunja);

  const orderTableAtom = atom((get) => {
    const tables = get(tablesAtom);
    const orders = tables['private/orders'] || {};
    return mapTable(orders, PswapOrder_fromJsonValue);
  });

  const ordersAtom = atom((get) => {
    const orderTable = get(orderTableAtom);
    return Object.entries(orderTable)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([, value]) => value);
  });

  bunja.effect(() => subscribe('private/orders', { rows: ['*'] }));

  return { ordersAtom };
});

export const ordersBunja = bunja(() => {
  const accountId = bunja.use(SubAccountIdScope);
  const base = bunja.will(baseOrdersBunja);
  const account = bunja.will(accountOrdersBunja);
  const { ordersAtom } = accountId != null ? account() : base();
  return { ordersAtom };
});

const openPositionTypes: ConditionalOrderType[] = [
  'CONDITIONAL_ORDER_TYPE_POSITION_TP',
  'CONDITIONAL_ORDER_TYPE_POSITION_SL',
];

export const pendingPositionsBunja = bunja(() => {
  const { ordersAtom } = bunja.use(ordersBunja);

  const pendingPositionsAtom = atom((get) => {
    const orders = get(ordersAtom);

    return orders
      .reduce((acc: PendingPosition[], o) => {
        if (openPositionTypes.includes(o.conditionalOrderType)) return acc;

        const qty = o.qty ?? zero;
        const amount = o.amount ?? zero;
        const orderVolumeType: PendingPosition['orderVolumeType'] = qty.isZero() ? 'amount' : 'qty';

        acc.push({
          ...o,
          orderVolumeType,
          qty,
          amount,
        });
        return acc;
      }, [])
      .sort(positionOrderSort)
      .reduce((acc: Record<string, PendingPosition[]>, p) => {
        acc[p.symbol] ??= [];
        acc[p.symbol].push(p);
        return acc;
      }, {});
  });
  pendingPositionsAtom.debugLabel = 'pendingPositionsAtom';

  const ordersMapAtom = atom((get) => {
    const orders = get(ordersAtom);
    return orders.reduce((acc: Record<string, PswapOrder[]>, o) => {
      acc[o.symbol] ??= [];
      acc[o.symbol].push(o);
      return acc;
    }, {});
  });
  ordersMapAtom.debugLabel = 'ordersMapAtom';

  return { ordersAtom, pendingPositionsAtom, ordersMapAtom };
});
pendingPositionsBunja.debugLabel = 'pendingPositionsBunja';
